import { ActionType, EntityRegionModeOutput, TenantCredentialId } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useMutation } from "@tanstack/react-query";
import type { inferProcedureOutput } from "@trpc/server";
import { ContentFull, ContentHeader } from "components/layout";
import { linkStyle } from "components/reports/show/custom-reports/custom-reports-sidebar";
import { trpc } from "components/rpc/trpc";
import { Badge } from "components/ui/badge";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { InlineBanner } from "components/ui/inline-banner";
import { PrimaryButton, primaryClasses } from "components/ui/primary-button";
import {
    RegionVisibility,
    RegionVisibilityTree,
} from "components/ui/region-visibility-tree";
import { SecondaryButton } from "components/ui/secondary-button";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useProductName } from "store/selectors/branding";
import { useCurrentRegion, useIsMultiRegionUser } from "store/selectors/hooks";
import { useHipaaAcknowledgement } from "./use-hipaa-acknowledgement";

export type QuickBooksCredential = NonNullable<
    inferProcedureOutput<AppRouter["integrations"]["getQuickBooksCredential"]>
>;

interface QuickBooksConnectionProps {
    credential: QuickBooksCredential;
    onDeleted: (id: TenantCredentialId) => void;
    onSaved: () => void;
    onConnect?: () => void;
    regionId?: string;
    activeForRegionName?: string;
}

export const QuickBooksConnection: React.FC<QuickBooksConnectionProps> = props => {
    const { credential, onDeleted, onSaved, onConnect, regionId, activeForRegionName } =
        props;
    const currentRegion = useCurrentRegion();
    const isMultiRegion = useIsMultiRegionUser();

    const trpcClient = trpc.useTRPC();
    const deleteCredentialMutation = useMutation({
        ...trpcClient.integrations.deleteCredential.mutationOptions(),
    });
    const updateQuickBooksCredentialMutation = useMutation({
        ...trpcClient.integrations.updateQuickBooksCredential.mutationOptions(),
    });
    const productName = useProductName();
    const { confirmHipaaRisk } = useHipaaAcknowledgement();

    const isInherited =
        (credential.regionVisibility?.regions?.length ?? 0) > 2 &&
        !!regionId &&
        !!onConnect &&
        !!credential.regionVisibility?.regions?.every(
            r => r.regionId !== regionId || r.mode === EntityRegionModeOutput.Inherited,
        );

    const editable = !!credential.regionVisibility?.editable;

    const credentialRegions = useMemo(
        () =>
            (credential.regionVisibility?.regions ?? []).map(r => ({
                regionId: r.regionId,
                mode: r.mode,
            })) as RegionVisibility[],
        [credential.regionVisibility?.regions],
    );

    const [localRegions, setLocalRegions] =
        useState<RegionVisibility[]>(credentialRegions);

    useEffect(() => {
        setLocalRegions(credentialRegions);
    }, [credentialRegions]);

    const isDirty = useMemo(() => {
        if (localRegions.length !== credentialRegions.length) return true;
        const sortedLocal = [...localRegions].sort((a, b) =>
            a.regionId.localeCompare(b.regionId),
        );
        const sortedServer = [...credentialRegions].sort((a, b) =>
            a.regionId.localeCompare(b.regionId),
        );
        return sortedLocal.some(
            (r, i) =>
                r.regionId !== sortedServer[i].regionId ||
                r.mode !== sortedServer[i].mode,
        );
    }, [localRegions, credentialRegions]);

    const handleSave = useCallback(async () => {
        const confirmed = await fancyConfirm(
            "Update Office Visibility",
            "Are you sure you want to change which offices can see this QuickBooks connection? This will take effect immediately for all affected users.",
            "Yes, save changes",
            "Cancel",
        );

        if (!confirmed) return;

        await updateQuickBooksCredentialMutation.mutateAsync({
            id: credential.id,
            regionVisibility: { regions: localRegions },
        });
        onSaved();
    }, [credential.id, localRegions, onSaved, updateQuickBooksCredentialMutation]);

    const handleReset = useCallback(() => {
        setLocalRegions(credentialRegions);
    }, [credentialRegions]);

    const handleReconnect = useCallback(async () => {
        const confirmed = await confirmHipaaRisk("QuickBooks");
        if (confirmed) {
            window.location.href = `/api/auth/quickbooks/reconnect?credentialId=${credential.id}`;
        }
    }, [confirmHipaaRisk, credential.id]);

    const handleDisconnect = useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();

            const confirmed = await fancyConfirm(
                "Remove QuickBooks",
                `Are you sure you want to disconnect QuickBooks? Your QuickBooks invoices will still be available in ${productName}, but they will no longer be synchronized automatically.`,
                "Yes, disconnect",
                "Cancel",
            );

            if (confirmed && credential?.id) {
                await deleteCredentialMutation.mutateAsync({
                    id: credential.id,
                });
                onDeleted(credential.id);
            }
        },
        [credential?.id, deleteCredentialMutation, onDeleted, productName],
    );

    return (
        <>
            <ContentFull>
                <ContentHeader>
                    <div className="flex items-center gap-2">
                        {`QuickBooks Realm ID: ${credential.realmId}`}{" "}
                        {isMultiRegion && activeForRegionName ? (
                            <Badge
                                value={`Active for ${activeForRegionName}`}
                                type="success"
                            />
                        ) : null}
                    </div>
                </ContentHeader>
                <div className="space-y-4">
                    {isInherited ? (
                        <InlineBanner type="info">
                            This QuickBooks connection is managed by a parent office and
                            shared with the {currentRegion.name} office.
                        </InlineBanner>
                    ) : null}
                    {credential.status === "Active" ? (
                        <InlineBanner type="success">
                            QuickBooks is connected
                        </InlineBanner>
                    ) : (
                        <>
                            <InlineBanner type="error">
                                QuickBooks is no longer connected to {productName}. Please
                                click <strong>Reconnect QuickBooks</strong> below to
                                reconnect. You will be taken to the QuickBooks website to
                                reauthorize access. Once that is complete, you will be
                                returned to {productName}.
                            </InlineBanner>
                            <div>
                                <button
                                    onClick={handleReconnect}
                                    className={primaryClasses}>
                                    Reconnect QuickBooks
                                </button>
                            </div>
                        </>
                    )}
                    {editable ? (
                        <>
                            <RegionVisibilityTree
                                actionType={ActionType.ManageIntegrations}
                                editable={true}
                                regionVisibility={localRegions}
                                onChange={setLocalRegions}
                                entityName="QuickBooks connection"
                            />
                            {isDirty ? (
                                <div className="flex space-x-2">
                                    <PrimaryButton
                                        onClick={handleSave}
                                        disabled={
                                            updateQuickBooksCredentialMutation.isPending ||
                                            localRegions.length === 0
                                        }>
                                        {updateQuickBooksCredentialMutation.isPending
                                            ? "Saving..."
                                            : "Save"}
                                    </PrimaryButton>
                                    <SecondaryButton
                                        onClick={handleReset}
                                        disabled={
                                            updateQuickBooksCredentialMutation.isPending
                                        }>
                                        Reset
                                    </SecondaryButton>
                                </div>
                            ) : null}
                        </>
                    ) : null}
                    <button onClick={handleDisconnect} className={linkStyle}>
                        <span className="text-sm">Remove integration</span>
                    </button>
                </div>
            </ContentFull>
            {isInherited ? (
                <ContentFull>
                    <ContentHeader>
                        Connect specific account for the {currentRegion.name} office
                    </ContentHeader>
                    <div className="space-y-2">
                        <p className="text-sm text-gray-600">
                            You can connect a separate QuickBooks account just for the{" "}
                            {currentRegion.name} office. This will replace the shared
                            connection for this office only.
                        </p>
                        <div>
                            <button onClick={onConnect} className={primaryClasses}>
                                Connect to QuickBooks
                            </button>
                        </div>
                    </div>
                </ContentFull>
            ) : null}
        </>
    );
};
